import type { FileInfo } from "@exagens/behavioral-insight-console-client";
import type { AppDispatch, AppStore } from "@/store/app/storeTypes";
import {
  needsSearchIndexRefetch,
  fileInfoFingerprint,
} from "../../../catalog/catalogFingerprint";
import { shouldRefetchBlueprintFromServer } from "../../../catalog/catalogVersioning";
import {
  createSearchIndexRunHandle,
  releaseSearchIndexingUi,
} from "./searchIndexRunLifecycle";
import {
  clearSparxBlueprintFetchFailure,
  recordSparxBlueprintFetchFailure,
} from "@/store/sparxsCatalog/blueprintFetchFailuresSlice";
import {
  indexRunProgressed,
  ingestSearchIndexRow,
  pruneSearchIndexRows,
} from "@/store/sparxsCatalog/searchIndexSlice";
import { isSparxBlueprintNotFoundError } from "../../../blueprint/sparxBlueprintNotFoundError";
import {
  fetchBlueprintForIndexPass,
  toNetworkFetchFailureMessage,
  tryBuildSearchIndexRowFromBlueprint,
} from "./runSearchIndexPassHelpers";

export type SearchIndexRunIdRef = { current: number };

export type RunSearchIndexPassParams = {
  store: AppStore;
  dispatch: AppDispatch;
  getActiveFileName: () => string | undefined;
  indexRunIdRef: SearchIndexRunIdRef;
};

const INDEX_CONCURRENCY = 6;

const BLUEPRINT_INDEX_MAX_ATTEMPTS = 3;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** Exported for unit tests; drives indexer network-queue ordering vs catalog order. */
export const orderFileNamesForIndex = (
  files: FileInfo[],
  activeFileName: string | undefined,
): string[] => {
  const names = files.map((f) => f.fileName);
  if (activeFileName == null || activeFileName.length === 0) {
    return names;
  }
  const idx = names.indexOf(activeFileName);
  if (idx <= 0) {
    return names;
  }
  const next = [...names];
  const [picked] = next.splice(idx, 1);
  next.unshift(picked);
  return next;
};

export const runSearchIndexPass = (
  params: RunSearchIndexPassParams,
): (() => void) | undefined => {
  const { store, dispatch, getActiveFileName, indexRunIdRef } = params;

  const catalogFiles = store.getState().editor.sparxsList.data;

  if (catalogFiles.length === 0) {
    if (store.getState().editor.sparxsSearchIndex.isIndexing) {
      releaseSearchIndexingUi(dispatch);
    }
    return undefined;
  }

  const runId = ++indexRunIdRef.current;

  const abortController = new AbortController();
  const { signal } = abortController;

  const orderedNames = orderFileNamesForIndex(
    catalogFiles,
    getActiveFileName(),
  );
  const fileByName = new Map(catalogFiles.map((f) => [f.fileName, f] as const));
  const titleByFileName = new Map(
    catalogFiles.map((f) => [f.fileName, f.title] as const),
  );
  const keepFileNames = catalogFiles.map((f) => f.fileName);

  const rows = store.getState().editor.sparxsSearchIndex.rowsByFileName;
  const cacheByFile = store.getState().editor.sparxsBlueprintCache.byFileName;
  const failuresByFileName =
    store.getState().editor.sparxsBlueprintFetchFailures.byFileName;

  const networkWork: string[] = [];
  const localWork: string[] = [];

  for (const fileName of orderedNames) {
    const file = fileByName.get(fileName);
    if (file == null) {
      continue;
    }
    const row = rows[fileName];
    const cache = cacheByFile[fileName];
    if (!needsSearchIndexRefetch(file, row, cache)) {
      continue;
    }
    if (shouldRefetchBlueprintFromServer(file, cache)) {
      if (failuresByFileName[fileName]?.notFound === true) {
        continue;
      }
      networkWork.push(fileName);
    } else if (cache != null) {
      localWork.push(fileName);
    }
  }

  dispatch(pruneSearchIndexRows({ keepFileNames }));

  const totalWork = networkWork.length + localWork.length;
  if (totalWork === 0) {
    if (store.getState().editor.sparxsSearchIndex.isIndexing) {
      releaseSearchIndexingUi(dispatch);
    }
    return () => {
      abortController.abort();
    };
  }

  const indexRun = createSearchIndexRunHandle({
    dispatch,
    runId,
    getCurrentRunId: () => indexRunIdRef.current,
  });

  indexRun.start({
    totalFiles: totalWork,
    clearRows: false,
  });

  const runLocal = () => {
    for (const fileName of localWork) {
      if (signal.aborted) {
        break;
      }
      const file = fileByName.get(fileName);
      const cache =
        store.getState().editor.sparxsBlueprintCache.byFileName[fileName];
      if (file == null || cache == null) {
        continue;
      }
      const title = titleByFileName.get(fileName) ?? fileName;
      const built = tryBuildSearchIndexRowFromBlueprint({
        blueprint: cache.blueprint,
        fileName,
        title,
        key: file.key,
        listFingerprint: fileInfoFingerprint(file),
        listLastModified: file.lastModified,
      });
      if (built.ok) {
        dispatch(ingestSearchIndexRow(built.row));
      } else {
        dispatch(
          recordSparxBlueprintFetchFailure({
            fileName,
            lastErrorMessage: built.message,
          }),
        );
      }
      dispatch(indexRunProgressed());
    }
  };

  const networkQueue = [...networkWork];

  const run = async () => {
    try {
      runLocal();

      const worker = async () => {
        while (!signal.aborted && networkQueue.length > 0) {
          const fileName = networkQueue.shift();
          if (fileName == null) {
            break;
          }
          const file = fileByName.get(fileName);
          if (file == null) {
            continue;
          }

          try {
            const outcome = await fetchBlueprintForIndexPass({
              fileName,
              store,
              signal,
              maxAttempts: BLUEPRINT_INDEX_MAX_ATTEMPTS,
              sleepMs: sleep,
            });

            switch (outcome.kind) {
              case "aborted":
                return;
              case "failed": {
                const message = toNetworkFetchFailureMessage(outcome.lastError);
                const notFound = isSparxBlueprintNotFoundError(
                  outcome.lastError,
                );
                dispatch(
                  recordSparxBlueprintFetchFailure({
                    fileName,
                    ...(message != null && message !== ""
                      ? { lastErrorMessage: message }
                      : {}),
                    ...(notFound ? { notFound: true } : {}),
                  }),
                );
                dispatch(indexRunProgressed());
                break;
              }
              case "ok": {
                dispatch(clearSparxBlueprintFetchFailure(fileName));
                const title = titleByFileName.get(fileName) ?? fileName;
                const built = tryBuildSearchIndexRowFromBlueprint({
                  blueprint: outcome.resolved.blueprint,
                  fileName,
                  title,
                  key: file.key,
                  listFingerprint: fileInfoFingerprint(file),
                  listLastModified: file.lastModified,
                });
                if (built.ok) {
                  dispatch(ingestSearchIndexRow(built.row));
                } else {
                  dispatch(
                    recordSparxBlueprintFetchFailure({
                      fileName,
                      lastErrorMessage: built.message,
                    }),
                  );
                }
                dispatch(indexRunProgressed());
                break;
              }
            }
          } catch (error: unknown) {
            if (error instanceof Error && error.name === "AbortError") {
              return;
            }
          }
        }
      };

      await Promise.all(
        Array.from({ length: INDEX_CONCURRENCY }, () => worker()),
      );
    } finally {
      indexRun.finishIfCurrent();
    }
  };

  void run();

  return () => {
    abortController.abort();
    indexRun.releaseIndexingUi();
  };
};
